import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { scanInterpretationData } from '@/mocks/scanInterpretation';
import { recordScan } from '@/mocks/scanHistory';
import { createStudySession } from '@/mocks/studySessions';
import { useTheme } from '@/contexts/ThemeContext';
import { useAIPreferences } from '@/contexts/AIPreferencesContext';
import { useProgress } from '@/contexts/ProgressContext';
import LearningMode from '@/components/feature/LearningMode';
import Pressable from '@/components/base/Pressable';
import SaveSessionModal from '../components/SaveSessionModal';
import { useSavedAIResult, SavedLessonUnavailable } from '@/lib/useSavedAIResult';

/* ── Step line type parser ── */
type LineType = 'goal' | 'def' | 'concept' | 'formula' | 'note' | 'science' | 'calc' | 'why' | 'text';

interface ParsedLine {
  type: LineType;
  text: string;
}

function parseLineType(line: string): ParsedLine {
  const trimmed = line.trim();
  if (trimmed.startsWith('GOAL:')) return { type: 'goal', text: trimmed.slice(5).trim() };
  if (trimmed.startsWith('DEF:')) return { type: 'def', text: trimmed.slice(4).trim() };
  if (trimmed.startsWith('CONCEPT:')) return { type: 'concept', text: trimmed.slice(8).trim() };
  if (trimmed.startsWith('FORMULA:')) return { type: 'formula', text: trimmed.slice(8).trim() };
  if (trimmed.startsWith('NOTE:')) return { type: 'note', text: trimmed.slice(5).trim() };
  if (trimmed.startsWith('SCIENCE:')) return { type: 'science', text: trimmed.slice(8).trim() };
  if (trimmed.startsWith('CALC:')) return { type: 'calc', text: trimmed.slice(5).trim() };
  if (trimmed.startsWith('WHY:')) return { type: 'why', text: trimmed.slice(4).trim() };
  return { type: 'text', text: trimmed };
}

/* ── Inline bold parser ── */
function parseBold(text: string, isDark: boolean): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    parts.push(
      <strong key={`b-${key++}`} className={`font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
  }
  return parts.length > 0 ? parts : text;
}

/* ── Section config map ── */
const sectionConfig: Record<
  Exclude<LineType, 'goal' | 'text'>,
  { emoji: string; label: string; bg: string; border: string; text: string; icon: string; iconColor: string }
> = {
  def: {
    emoji: '📘',
    label: 'Definition',
    bg: 'bg-blue-50/80 dark:bg-blue-500/[0.04]',
    border: 'border-blue-100 dark:border-blue-500/10',
    text: 'text-blue-800 dark:text-blue-300/90',
    icon: 'ri-book-open-line',
    iconColor: 'text-blue-500',
  },
  concept: {
    emoji: '🧠',
    label: 'Key Concept',
    bg: 'bg-violet-50/80 dark:bg-violet-500/[0.04]',
    border: 'border-violet-100 dark:border-violet-500/10',
    text: 'text-violet-800 dark:text-violet-300/90',
    icon: 'ri-lightbulb-line',
    iconColor: 'text-violet-500',
  },
  formula: {
    emoji: '⚡',
    label: 'Formula',
    bg: 'bg-neutral-100/80 dark:bg-white/[0.03]',
    border: 'border-neutral-200 dark:border-white/[0.06]',
    text: 'text-neutral-800 dark:text-neutral-300',
    icon: 'ri-function-line',
    iconColor: 'text-neutral-500',
  },
  note: {
    emoji: '📌',
    label: 'Important Note',
    bg: 'bg-amber-50/80 dark:bg-amber-500/[0.04]',
    border: 'border-amber-100 dark:border-amber-500/10',
    text: 'text-amber-800 dark:text-amber-300/90',
    icon: 'ri-pushpin-line',
    iconColor: 'text-amber-500',
  },
  science: {
    emoji: '🧪',
    label: 'Science Explanation',
    bg: 'bg-emerald-50/80 dark:bg-emerald-500/[0.04]',
    border: 'border-emerald-100 dark:border-emerald-500/10',
    text: 'text-emerald-800 dark:text-emerald-300/90',
    icon: 'ri-flask-line',
    iconColor: 'text-emerald-500',
  },
  calc: {
    emoji: '📐',
    label: 'Calculation',
    bg: 'bg-slate-50/80 dark:bg-slate-500/[0.04]',
    border: 'border-slate-200 dark:border-slate-500/10',
    text: 'text-slate-800 dark:text-slate-300',
    icon: 'ri-equalizer-line',
    iconColor: 'text-slate-500',
  },
  why: {
    emoji: '💡',
    label: 'Why',
    bg: 'bg-sky-50/80 dark:bg-sky-500/[0.04]',
    border: 'border-sky-100 dark:border-sky-500/10',
    text: 'text-sky-800 dark:text-sky-300/90',
    icon: 'ri-question-line',
    iconColor: 'text-sky-500',
  },
};

export default function ScanResultText() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { preferences } = useAIPreferences();
  const { recordQuestionSolved, checkAndUpdateStreak } = useProgress();
  const containerRef = useRef<HTMLDivElement>(null);

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-[#f8f8f8]',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    iconBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-100',
    headerBg: isDark ? 'bg-[#0F0F10]/90' : 'bg-[#f8f8f8]/90',
    headerBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-200',
    divider: isDark ? 'border-white/[0.03]' : 'border-neutral-100',
    stepNumBg: isDark ? 'bg-neutral-900 border-neutral-800' : 'bg-neutral-100 border-neutral-200',
    stepNumText: isDark ? 'text-white' : 'text-neutral-900',
  };

  const { data: savedResult, loading, generationId } = useSavedAIResult();
  const data = savedResult || scanInterpretationData;
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLearningMode, setShowLearningMode] = useState(false);

  // Show save modal when arriving from generation flow
  useEffect(() => {
    const awaiting = sessionStorage.getItem('scan_awaiting_save') === 'true';
    if (awaiting) {
      setShowSaveModal(true);
    }
  }, []);

  const handleSaveSession = (customName: string) => {
    sessionStorage.removeItem('scan_awaiting_save');
    createStudySession({
      title: customName,
      subject: data.subject,
      topicName: data.overallSubject.topicsDetected[0],
      type: 'text',
      uploadType: 'Image',
    });
    recordScan({
      generationId: data.id,
      status: 'completed',
      type: 'text',
      title: customName,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setShowSaveModal(false);
    // Record progress only when user explicitly saves
    checkAndUpdateStreak();
    data.detectedQuestions.forEach(() => {
      recordQuestionSolved(
        data.subject.toLowerCase().replace(/\s+/g, '-'),
        data.subject,
        85 + Math.floor(Math.random() * 15),
      );
    });
    setTimeout(() => setShowSaveToast(false), 2500);
  };

  const handleSkipSave = () => {
    sessionStorage.removeItem('scan_awaiting_save');
    setShowSaveModal(false);
  };

  const handleManualSave = () => {
    recordScan({
      generationId: data.id,
      status: 'completed',
      type: 'text',
      title: data.title,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2200);
  };

  const handleCopy = () => {
    const text = data.detectedQuestions
      .map((q, i) => `Q${q.number}: ${q.text}\n${data.explanation.steps[i]?.content.join('\n') || ''}\n`)
      .join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: `${data.title} — LAVYN`,
        text: `AI solution for ${data.subject}: ${data.title}`,
      });
    } catch {
      // share not available or cancelled
    }
  };

  const handleBack = () => {
    navigate('/history');
  };

  /* ── Step line renderer ── */
  const renderStepLine = (line: string, lineIdx: number, qIdx: number) => {
    const { type, text } = parseLineType(line);
    const key = `step-${qIdx}-${lineIdx}`;

    if (type === 'goal') {
      return (
        <div key={key} className="mb-2 mt-1">
          <p className={`text-[13px] font-semibold ${t.textPrimary} leading-relaxed`}>
            {parseBold(text, isDark)}
          </p>
        </div>
      );
    }

    if (type === 'text') {
      return (
        <p key={key} className={`text-[13px] leading-[1.75] ${t.textSecondary} my-1.5`}>
          {parseBold(text, isDark)}
        </p>
      );
    }

    // For "Simple" style, compress sections into plain text
    if (preferences.explanationStyle === 'Simple' && (type === 'note' || type === 'why')) {
      return (
        <p key={key} className={`text-[13px] leading-[1.75] ${t.textSecondary} my-1.5`}>
          <span className={`font-medium ${t.textPrimary}`}>{sectionConfig[type].label}:</span>{' '}
          {parseBold(text, isDark)}
        </p>
      );
    }


    // Tagged sections: def, concept, formula, note, science, calc, why
    const config = sectionConfig[type];
    const isFormula = type === 'formula';
    const isCalc = type === 'calc';

    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: lineIdx * 0.04, duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        className={`my-2 rounded-xl border ${config.border} ${config.bg} overflow-hidden`}
      >
        {/* Section header row */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-1">
          <span className="text-sm">{config.emoji}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text} opacity-60`}>
            {config.label}
          </span>
        </div>

        {/* Content */}
        <div className={`px-4 ${isFormula || isCalc ? 'pb-3 pt-1 text-center' : 'pb-3 pt-0.5'}`}>
          <p
            className={`${
              isFormula || isCalc
                ? 'text-[15px] font-mono leading-relaxed text-neutral-800 dark:text-neutral-200'
                : `text-[13px] leading-[1.7] ${config.text}`
            }`}
          >
            {parseBold(text, isDark)}
          </p>
        </div>
      </motion.div>
    );
  };

  if (loading || (generationId && !savedResult)) return <SavedLessonUnavailable loading={loading} />;
  return (
    <div className={`flex flex-col min-h-full ${t.pageBg} transition-colors duration-500 relative`}>
      {/* Save Toast */}
      <AnimatePresence>
        {showSaveToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="absolute top-0 left-0 right-0 z-50 flex justify-center pt-4"
          >
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full ${isDark ? 'bg-neutral-800 border border-white/[0.08]' : 'bg-white border border-neutral-200'} shadow-lg`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-500 text-sm" />
              </div>
              <span className={`text-xs font-medium ${t.textSecondary}`}>Saved to History</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Header */}
      <div className={`sticky top-0 z-30 ${t.headerBg} backdrop-blur-xl border-b ${t.headerBorder} transition-colors duration-500`}>
        <div className="flex items-center justify-between px-5 py-4">
          <Pressable
            onClick={handleBack}
            className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`ri-arrow-left-line ${t.textMuted}`} />
            </div>
          </Pressable>
          <div className="text-center">
            <h1 className={`text-sm font-bold ${t.textPrimary}`}>Text Solution</h1>
            <p className={`text-[11px] ${t.textSecondary}`}>{data.subject}</p>
          </div>
          <Pressable
            onClick={handleManualSave}
            className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${saved ? 'ri-check-fill text-emerald-500' : `ri-bookmark-line ${t.textMuted}`}`} />
            </div>
          </Pressable>
        </div>
      </div>

      {/* Scrollable Content */}
      <div ref={containerRef} className="flex-1 overflow-y-auto min-h-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="px-5 pt-4 pb-8 space-y-5"
        >
          {/* AI Style Badge */}
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isDark ? 'border-white/[0.06] text-[#6E6E73]' : 'border-black/[0.08] text-black/40'}`}>
              {preferences.explanationStyle}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isDark ? 'border-white/[0.06] text-[#6E6E73]' : 'border-black/[0.08] text-black/40'}`}>
              {preferences.learningMode}
            </span>
          </div>

          {/* Clean extracted question — the upload remains input-only. */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 transition-colors duration-500`}
          >
            <div className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] ${t.textMuted}`}>Extracted question</div>
            <p className={`whitespace-pre-wrap text-lg leading-relaxed ${t.textPrimary}`}>
              {data.cleanQuestion || data.detectedQuestions[0]?.text || data.extractedText}
            </p>
          </motion.div>

          {/* Success Banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            className={`rounded-2xl border ${isDark ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-emerald-200 bg-emerald-50/70'} p-4 flex items-center gap-3`}
          >
            <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-100'} flex items-center justify-center flex-shrink-0`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-line text-emerald-500 text-lg" />
              </div>
            </div>
            <div>
              <p className={`text-sm font-bold ${t.textPrimary}`}>AI Analysis Complete</p>
              <p className={`text-[11px] ${t.textMuted}`}>Step-by-step solutions ready for all {data.detectedQuestions.length} problems</p>
            </div>
          </motion.div>

          {/* Confidence Score */}
          {preferences.showConfidenceIndicators && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className={`rounded-2xl border ${isDark ? 'border-blue-500/15 bg-blue-500/[0.03]' : 'border-blue-200 bg-blue-50/50'} p-4 flex items-center gap-3`}
            >
              <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'} flex items-center justify-center flex-shrink-0`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-check-line text-blue-500 text-lg" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-bold ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Confidence: High</p>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>94%</span>
                </div>
                <p className={`text-[11px] ${t.textMuted} mt-0.5`}>AI is confident in these solutions. Always verify with your teacher.</p>
              </div>
            </motion.div>
          )}

          {/* Detected Questions with Premium Step Solutions */}
          {data.detectedQuestions.map((q, qIdx) => {
            const step = data.explanation.steps[qIdx];
            if (!step) return null;
            return (
              <motion.div
                key={q.number}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + qIdx * 0.12, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} overflow-hidden transition-colors duration-500`}
              >
                {/* Problem Header */}
                <div className={`p-5 border-b ${t.divider}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${t.stepNumBg} border flex items-center justify-center flex-shrink-0`}>
                      <span className={`text-sm font-bold ${t.stepNumText}`}>{q.number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-semibold ${t.textMuted} uppercase tracking-wider mb-1.5`}>{q.questionType}</p>
                      {/* Centered Problem Equation */}
                      <div className={`rounded-xl border ${isDark ? 'border-white/[0.05] bg-white/[0.02]' : 'border-neutral-100 bg-neutral-50/80'} px-4 py-3 text-center`}>
                        <p className="text-[16px] font-mono text-neutral-800 dark:text-neutral-200 leading-relaxed">
                          {q.text}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step-by-Step Content */}
                <div className="p-5 space-y-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-list-check-2 ${t.textMuted} text-xs`} />
                    </div>
                    <h4 className={`text-xs font-bold ${t.textPrimary} uppercase tracking-wider`}>Step-by-Step Solution</h4>
                  </div>

                  {step.content.map((line, lineIdx) => renderStepLine(line, lineIdx, qIdx))}

                  {/* Final Answer Card — Centered & Prominent */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + step.content.length * 0.04 }}
                    className={`mt-5 rounded-2xl border ${isDark ? 'border-emerald-500/15 bg-emerald-500/[0.04]' : 'border-emerald-200/80 bg-emerald-50/80'} p-5 text-center`}
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-base">✅</span>
                      <span className={`text-[11px] font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'} uppercase tracking-wider`}>Final Answer</span>
                    </div>
                    <p className={`text-[28px] font-bold font-mono ${isDark ? 'text-emerald-300' : 'text-emerald-700'} leading-tight`}>
                      {step.result}
                    </p>
                  </motion.div>
                </div>

                {/* Key Terms */}
                <div className="px-5 pb-5 pt-0">
                  <div className="flex flex-wrap gap-1.5">
                    {q.keyTerms.map((term) => (
                      <span
                        key={term}
                        className={`text-[10px] px-2.5 py-1 rounded-md border ${isDark ? 'border-white/[0.05] bg-white/[0.03]' : 'border-neutral-100 bg-neutral-50'} ${t.textMuted}`}
                      >
                        {term}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 transition-colors duration-500`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-base">🧠</span>
              <h3 className={`text-sm font-bold ${t.textPrimary}`}>Summary</h3>
            </div>
            <p className={`text-sm ${t.textSecondary} leading-relaxed`}>{data.explanation.summary}</p>
          </motion.div>

          {/* Follow-up Questions */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.4 }}
            className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 transition-colors duration-500`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-base">💬</span>
              <h3 className={`text-sm font-bold ${t.textPrimary}`}>Questions to Ask AI</h3>
            </div>
            <div className="space-y-2">
              {data.explanation.followUpQuestions.map((question) => (
                <Pressable
                  key={question}
                  onClick={() => navigate('/ai-assistant')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border ${isDark ? 'border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05]' : 'border-neutral-100 bg-neutral-50 hover:bg-neutral-100'} transition-colors duration-200 text-left`}
                  scale={0.97}
                >
                  <div className="w-7 h-7 rounded-lg bg-neutral-900 flex items-center justify-center flex-shrink-0">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bard-line text-white text-[10px]" />
                    </div>
                  </div>
                  <p className={`text-xs ${t.textSecondary} flex-1`}>{question}</p>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-arrow-right-s-line ${t.textFaint} text-sm`} />
                  </div>
                </Pressable>
              ))}
            </div>
          </motion.div>

          {/* Bottom Actions */}
          <div className="space-y-3 pt-2">
            {preferences.autoLearningMode && (
              <Pressable
                onClick={() => setShowLearningMode(true)}
                className={`w-full ${isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-700'} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 border`}
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-sm" />
                </div>
                <span className="whitespace-nowrap">Continue Learning</span>
              </Pressable>
            )}
            <Pressable
              onClick={() => navigate('/lesson/lesson-001')}
              className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2"
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-2-line text-sm" />
              </div>
              <span className="whitespace-nowrap">Watch Full Lesson</span>
            </Pressable>

            <div className="flex gap-3">
              <Pressable
                onClick={handleCopy}
                className={`flex-1 ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500`}
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${copied ? 'ri-check-line text-emerald-500' : `ri-file-copy-line ${t.textMuted}`} text-sm`} />
                </div>
                <span className={`${t.textPrimary} whitespace-nowrap`}>{copied ? 'Copied' : 'Copy'}</span>
              </Pressable>
              <Pressable
                onClick={handleShare}
                className={`flex-1 ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500`}
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-share-forward-line ${t.textMuted} text-sm`} />
                </div>
                <span className={`${t.textPrimary} whitespace-nowrap`}>Share</span>
              </Pressable>
            </div>

            <Pressable
              onClick={() => navigate('/scan/start')}
              className={`w-full ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500 ${t.textPrimary}`}
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-scan-line ${t.textMuted} text-sm`} />
              </div>
              <span className="whitespace-nowrap">Scan Another</span>
            </Pressable>
            <Pressable
              onClick={() => navigate('/history')}
              className={`w-full ${t.cardBg} border ${t.cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500 ${t.textPrimary}`}
              scale={0.97}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-history-line ${t.textMuted} text-sm`} />
              </div>
              <span className="whitespace-nowrap">View History</span>
            </Pressable>
          </div>
        </motion.div>
      </div>

      {/* Save Session Modal */}
      <SaveSessionModal
        open={showSaveModal}
        subject={data.subject}
        topicName={data.overallSubject.topicsDetected[0]}
        type="text"
        onSave={handleSaveSession}
        onSkip={handleSkipSave}
      />

      {/* Learning Mode Overlay */}
      <AnimatePresence>
        {showLearningMode && (
          <LearningMode
            subject={data.subject}
            topic={data.topic}
            questionCount={data.detectedQuestions.length}
            onClose={() => setShowLearningMode(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}